import { Decorative } from "@faire/design-tokens";
import { trackFaireDirectFaireDirectLandingPageLoadView } from "@faire/web-api/events/faireDirect/view/faireDirectLandingPageLoad";
import { trackFaireDirectSignUpLandingPageView } from "@faire/web-api/events/faireDirect/view/signUpLandingPage";
import { IBrand } from "@faire/web-api/indigofair/data/IBrand";
import { QueryParameters as FaireDirectQueryParameters } from "@faire/web-api/routes/user/sign-up";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { throttleIfPossible } from "@faire/web/common/globals/throttleIfPossible";
import { Path } from "@faire/web/common/Path";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { trackEvent } from "@faire/web/common/trackEvent";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import * as React from "react";
import { useRef, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { SAMPLE_PROSPECTING_SIGN_IN_QUERY_PARAM_VALUE } from "@faire/retailer/components/App/AppModals/consts";
import { FooterSEOContent } from "@faire/retailer/components/Footer/FooterSEOContent";
import { PassiveScroll } from "@faire/retailer/components/Scroll/Scroll";
import { MarketEventDirectSection } from "@faire/retailer/features/FaireMarkets/FaireDirect/MarketEventDirectSection";
import { ProspectingSampleBannerAndModals } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleBannerAndModals";
import { ProspectingSampleSignupFlow } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleSignupFlow";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { getExternalSalesRepName } from "@faire/retailer/serialized-data/getExternalSalesRepName";
import { MarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { PageWrapper } from "@faire/retailer/views/faire-direct/components/PageWrapper";
import { SignUpNavComponent } from "@faire/retailer/views/faire-direct/components/SignUpNavComponent";
import { BrandPreviewSection } from "@faire/retailer/views/faire-direct/sections/BrandPreviewSection";
import { FooterSignUp } from "@faire/retailer/views/faire-direct/sections/FooterSignUp";
import { IntroSection } from "@faire/retailer/views/faire-direct/sections/IntroSection";
import { NewsSection } from "@faire/retailer/views/faire-direct/sections/NewsSection";
import { PerksSection } from "@faire/retailer/views/faire-direct/sections/PerksSection/PerksSection";
import { QuoteSection } from "@faire/retailer/views/faire-direct/sections/QuoteSection";

import { useFaireDirectLandingPagePopSignUpEffect } from "./__internal__/useFaireDirectLandingPagePopSignUpEffect";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const FaireDirectLandingPage = ({
  faireDirectBrandToken,
  faireDirectBrand,
}: {
  faireDirectBrandToken: string;
  faireDirectBrand: IBrand;
}) => {
  useFaireDirectLandingPagePopSignUpEffect();

  const { signUp, email } = useQueryParams(FaireDirectQueryParameters.parse);
  const { oet } = useQueryParams((search?: string) => {
    const queryParams = search ? parseQueryParams(search) : undefined;
    const oet = queryParams?.get("oet") ?? undefined;
    return { oet };
  });

  const location = useLocation();
  const currentPath = new Path(location);
  const loginUrl = currentPath.append({ signIn: "1" }).toString();
  const isPreSample = signUp === SAMPLE_PROSPECTING_SIGN_IN_QUERY_PARAM_VALUE;

  const [showNav, setShowNav] = useState<boolean>(false);
  const signupReference = useRef<HTMLDivElement>(null);

  const [hasSeenIntro, setHasSeenIntro] = useState<boolean>(false);
  const [pageViewRecorded, setPageViewRecorded] = useState<boolean>(false);

  const signUpNavRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("signUpNav");
  });
  const introRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("intro");
    setHasSeenIntro(true);
  });
  const brandPreviewRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("brandPreview");
  });
  const perksRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("perks");
  });
  const eventRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("event");
  });
  const quotesRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("quotes");
  });
  const newsRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("news");
  });
  const footerSignUpRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("footerSignUp");
  });
  const footerRef = useDoActionWhenVisible(() => {
    trackSectionViewEvent("footer");
  });

  const { faireDirectBrandAlias } = useFaireDirectBrand();

  const handleOnScroll = useCallback(() => {
    if (signupReference && signupReference.current) {
      const { offsetTop, clientHeight } = signupReference.current;
      const isBelowSignup =
        getWindowOrThrow().scrollY > offsetTop + clientHeight;

      if (showNav !== isBelowSignup) {
        setShowNav(isBelowSignup);
      }
    }
  }, [showNav]);

  useEffect(() => {
    handleOnScroll();
  }, [handleOnScroll]);

  useEffect(() => {
    let cleanupTimeout: number | null = null;
    if (!pageViewRecorded && hasSeenIntro) {
      cleanupTimeout =
        setTimeoutIfPossible(() => {
          trackFaireDirectFaireDirectLandingPageLoadView(
            faireDirectBrandAlias ?? ""
          );
          setPageViewRecorded(true);
        }, 2000) ?? null;
    }
    return () => {
      if (cleanupTimeout != null) {
        clearTimeout(cleanupTimeout);
      }
    };
  }, [faireDirectBrandAlias, pageViewRecorded, hasSeenIntro]);

  useEffect(() => {
    trackFaireDirectSignUpLandingPageView(faireDirectBrandAlias ?? "");
    if (getExternalSalesRepName()) {
      trackEvent(
        `faire-direct-sign-up/landing-page/rep/${getExternalSalesRepName()}/view/source/${oet}`
      );
    } else {
      trackEvent(`faire-direct-sign-up/landing-page/view/source/${oet}`);
    }
  }, [oet, faireDirectBrandAlias]);

  if (isPreSample && email) {
    return (
      <PageWrapper loginUrl={loginUrl} isPreSample={isPreSample}>
        <ProspectingSampleSignupFlow brand={faireDirectBrand} email={email} />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper loginUrl={loginUrl} isPreSample={isPreSample}>
      <ProspectingSampleBannerAndModals
        isPreSample={isPreSample}
        brand={faireDirectBrand}
      />

      <RefContainer ref={signUpNavRef}>
        <SignUpNavComponent
          brandName={faireDirectBrand.name || ""}
          showNav={showNav}
          eventRoot="faire-direct-sign-up"
        />
      </RefContainer>

      <RefContainer ref={introRef}>
        <IntroSection
          brand={faireDirectBrand}
          isPreSample={isPreSample}
          innerRef={signupReference}
          loginUrl={loginUrl}
        />
      </RefContainer>

      <RefContainer ref={brandPreviewRef}>
        <BrandPreviewSection
          brand={faireDirectBrand}
          brandToken={faireDirectBrandToken}
          pageJsExecutedMark={PAGE_JS_EXECUTED_MARK}
        />
      </RefContainer>

      <RefContainer ref={perksRef}>
        <PerksSection />
      </RefContainer>

      {MarketStore.get().showFaireDirectBanner ? (
        <RefContainer ref={eventRef}>
          <MarketEventDirectSection brand={faireDirectBrand} />
        </RefContainer>
      ) : null}

      <RefContainer ref={quotesRef}>
        <QuoteSection backgroundColor={Decorative.tint.neutral} />
      </RefContainer>

      <RefContainer ref={newsRef}>
        <NewsSection />
      </RefContainer>

      <RefContainer ref={footerSignUpRef}>
        <FooterSignUp brand={faireDirectBrand} loginUrl={loginUrl} />
      </RefContainer>

      <RefContainer ref={footerRef}>
        <FooterSEOContent />
      </RefContainer>

      <PassiveScroll onScroll={throttleIfPossible(handleOnScroll, 200)} />
    </PageWrapper>
  );
};

const RefContainer = styled.div`
  width: 100%;
`;

const trackSectionViewEvent = (sectionKey: string) =>
  trackEvent(`fd-landing/scroll/${sectionKey}`);
